import { CommonModule } from '@angular/common';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  InputSignal,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DEFAULT_TOASTER_LIFE,
  ZendraLocalizationServices,
  ZnConfirmation,
  ZnMultiSelect,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { catchError, Observable, of, startWith, switchMap, tap } from 'rxjs';

import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { ContinuousInventoryRoutineModalWorkflow } from '../continuous-inventory-routine-modal-workflow';

interface UndoContinuousInventoryService {
  executeUndoContinuousInventory(request: {
    meditionNumber: number;
    standNumbers: string[];
  }): Observable<{ message?: string | null }>;
}

interface ContinuousInventoryUndoCatalogService {
  useLidar?: boolean;
  loadMeditionNumbers(
    params?: ZnSelectQueryParams,
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }>;
  loadContinuousInventoryStands(meditionNumber: number): Observable<{ key: string; value: string }[]>;
}

@Component({
  selector: 'lib-action-undo-continuous-inventory',
  imports: [CommonModule, LocalizationPipe, ReactiveFormsModule, ZnSingleSelect, ZnMultiSelect],
  templateUrl: './action-undo-continuous-inventory.html',
})
export class ActionUndoContinuousInventory implements IModalContent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);

  @ViewChild('standSelect') standSelect?: ZnMultiSelect;

  loading = signal(false);
  private readonly modalWorkflow = new ContinuousInventoryRoutineModalWorkflow({
    activeModal: this.activeModal,
    confirmationService: this.confirmationService,
    destroyRef: this.destroyRef,
    loading: this.loading,
    toaster: this.toaster,
  });
  completed = output<void>();
  selectedRecords?: InputSignal<any[]>;
  selectedRecordIds?: InputSignal<number[]>;

  @Input({ required: true }) undoService!: UndoContinuousInventoryService;
  @Input({ required: true }) catalogService!: ContinuousInventoryUndoCatalogService;
  @Input({ required: true }) measurementLabelKey!: string;
  @Input({ required: true }) standLabelKey!: string;
  @Input() successToastLife = DEFAULT_TOASTER_LIFE;

  readonly localizationResource = ZendraLocalizationServices.Inventory;
  readonly standOptions = signal<{ key: string; value: string }[]>([]);
  readonly selectedStandNumbers = signal<string[]>([]);
  readonly selectAllActive = signal(false);

  form: FormGroup = this.formBuilder.group({
    MeditionNumber: [null as number | null, Validators.required],
    StandNumbers: [[] as string[]],
  });

  private readonly formStatus = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), {
    initialValue: this.form.status,
  });

  canSave = computed(() => {
    this.formStatus();
    return (
      this.form.controls['MeditionNumber'].valid &&
      this.standOptions().length > 0 &&
      (this.selectAllActive() || this.selectedStandNumbers().length > 0) &&
      !this.loading()
    );
  });

  readonly meditionNumbers = (params?: ZnSelectQueryParams) => {
    this.catalogService.useLidar = false;
    return this.catalogService.loadMeditionNumbers(params);
  };

  constructor() {
    this.form.controls['MeditionNumber'].valueChanges
      .pipe(
        tap(() => this.resetStandSelection()),
        switchMap(value => {
          const meditionNumber = Number(value);
          if (!Number.isFinite(meditionNumber) || meditionNumber <= 0) {
            return of([] as { key: string; value: string }[]);
          }

          return this.catalogService.loadContinuousInventoryStands(meditionNumber).pipe(
            catchError(error => {
              console.error(error);
              return of([] as { key: string; value: string }[]);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(options => this.initializeStandSelection(options));
  }

  onStandSelectionChange(value: string | string[] | null): void {
    let values: string[] = [];
    if (Array.isArray(value)) {
      values = value;
    } else if (value) {
      values = [value];
    }
    const selected = values.map(item => item.trim()).filter(Boolean);
    const selectAll = !!this.standSelect?.allSelected() && this.standOptions().length > 0;

    this.selectedStandNumbers.set(selected);
    this.selectAllActive.set(selectAll);
    this.form.markAsDirty();
  }

  confirm(): void {
    if (!this.canSave() || this.loading()) {
      return;
    }

    const meditionNumber = Number(this.form.controls['MeditionNumber'].value);
    if (!Number.isFinite(meditionNumber) || meditionNumber <= 0) {
      return;
    }

    const standNumbers = this.selectAllActive() ? [] : this.selectedStandNumbers();

    this.loading.set(true);
    this.modalWorkflow.submit(
      this.undoService.executeUndoContinuousInventory({ meditionNumber, standNumbers }),
      () => this.successToastLife,
    );
  }

  dismiss(): void {
    this.modalWorkflow.dismiss(this.form.dirty, () => {
      this.form.reset({ MeditionNumber: null, StandNumbers: [] });
      this.resetStandSelection();
    });
  }

  private initializeStandSelection(options: { key: string; value: string }[]): void {
    this.standOptions.set(options);

    const allStandNumbers = options.map(option => option.key);
    this.selectedStandNumbers.set(allStandNumbers);
    this.selectAllActive.set(allStandNumbers.length > 0);
    this.form.controls['StandNumbers'].setValue(allStandNumbers);

    if (this.standSelect) {
      this.standSelect.allSelected.set(allStandNumbers.length > 0);
      this.standSelect.allSelectedSearchTerm.set(null);
    }
  }

  private resetStandSelection(): void {
    this.standOptions.set([]);
    this.selectedStandNumbers.set([]);
    this.selectAllActive.set(false);
    this.form.controls['StandNumbers'].setValue([], { emitEvent: false });
    this.standSelect?.reset();
  }
}
